import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';

interface LightboxProps {
  images: Array<{
    url: string;
    title: string;
    description: string;
    category: string;
  }>;
  currentIndex: number;
  onClose: () => void;
  onNavigate: (index: number) => void;
}

export default function Lightbox({ images, currentIndex, onClose, onNavigate }: LightboxProps) {
  const currentImage = images[currentIndex];

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrevious();
      if (e.key === 'ArrowRight') handleNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [currentIndex]);

  const handlePrevious = () => {
    onNavigate(currentIndex > 0 ? currentIndex - 1 : images.length - 1);
  };

  const handleNext = () => {
    onNavigate(currentIndex < images.length - 1 ? currentIndex + 1 : 0);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-charcoal/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-6 right-6 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer z-50"
      >
        <i className="ri-close-line text-white text-2xl"></i>
      </button>

      {/* Counter */}
      <div className="absolute top-6 left-6 text-white/80 text-sm font-medium z-50">
        {currentIndex + 1} / {images.length}
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          handlePrevious();
        }}
        className="absolute left-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer z-50"
      >
        <i className="ri-arrow-left-line text-white text-2xl"></i>
      </button>

      <button
        onClick={(e) => {
          e.stopPropagation();
          handleNext();
        }}
        className="absolute right-6 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors cursor-pointer z-50"
      >
        <i className="ri-arrow-right-line text-white text-2xl"></i>
      </button>

      {/* Image Container */}
      <div
        className="max-w-6xl max-h-[85vh] mx-auto px-20"
        onClick={(e) => e.stopPropagation()}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
            className="relative"
          >
            <img
              src={currentImage.url}
              alt={currentImage.title}
              title={currentImage.title}
              className="max-w-full max-h-[70vh] object-contain mx-auto rounded-lg shadow-2xl"
            />

            {/* Image Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mt-6 text-center"
            >
              <span className="inline-block px-4 py-1.5 bg-coral/20 text-coral rounded-full text-xs font-medium mb-3 capitalize">
                {currentImage.category.replace('-', ' ')}
              </span>
              <h3 className="text-white font-semibold text-2xl mb-2">{currentImage.title}</h3>
              <p className="text-white/70 text-sm">{currentImage.description}</p>
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Thumbnails */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 overflow-x-auto max-w-4xl px-6">
        {images.map((image, index) => (
          <button
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              onNavigate(index);
            }}
            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden transition-all cursor-pointer ${
              index === currentIndex
                ? 'ring-2 ring-coral scale-110'
                : 'opacity-50 hover:opacity-100'
            }`}
          >
            <img
              src={image.url}
              alt={image.title}
              className="w-full h-full object-cover object-top"
            />
          </button>
        ))}
      </div>
    </motion.div>
  );
}
